import { useState, useEffect } from 'react';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

/**
 * Custom hook for managing OAuth integrations
 * Implements the OAuth Integration API as documented
 * 
 * Supported providers: google, google_calendar, slack, notion
 * 
 * @returns {Object} OAuth hook state and methods
 */
export function useOAuthIntegrations() {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch integration status for the current user
   * GET /api/oauth/status
   */
  const fetchStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/oauth/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Not authorized');
        }
        throw new Error(data.message || 'Failed to fetch integration status');
      }

      if (data.success && data.data) {
        setIntegrations(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch integration status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Start OAuth flow for a provider
   * POST /api/oauth/start
   * 
   * @param {string} provider - Provider ID (google, slack, notion, google_calendar)
   * @param {Object} context - Optional context data to pass through OAuth flow
   */
  const connect = async (provider, context = {}) => {
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be logged in to connect');
      }

      const response = await fetch(`${API_BASE_URL}/oauth/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider, context })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Not authorized');
        }
        if (response.status === 400) {
          throw new Error(data.message || 'Provider not configured');
        }
        throw new Error(data.message || `Failed to start ${provider} connection`);
      }

      if (data.success && data.url) {
        // Redirect to OAuth provider
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'No OAuth URL returned');
      }
    } catch (err) {
      console.error(`Failed to connect ${provider}:`, err);
      setError(err.message);
      throw err; // Re-throw so caller can handle
    }
  };

  /**
   * Disconnect an integration
   * POST /api/oauth/disconnect
   * 
   * @param {string} provider - Provider ID to disconnect
   */
  const disconnect = async (provider) => {
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/oauth/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to disconnect');
      }

      if (data.success) {
        // Refresh status after successful disconnect
        await fetchStatus();
        return true;
      } else {
        throw new Error(data.message || 'Disconnect failed');
      }
    } catch (err) {
      console.error(`Failed to disconnect ${provider}:`, err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Manually refresh OAuth token
   * POST /api/oauth/refresh
   * 
   * @param {string} provider - Provider ID to refresh
   * @returns {Object} Result with requiresReauth flag if token can't be refreshed
   */
  const refresh = async (provider) => {
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider })
      });

      const data = await response.json();

      if (data.requiresReauth) {
        // Refresh token expired, user needs to reconnect
        return {
          success: false,
          requiresReauth: true,
          message: data.message || 'Please reconnect your account'
        };
      }

      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }

      if (data.success) {
        // Refresh status after successful token refresh
        await fetchStatus();
        return { success: true };
      }

      throw new Error(data.message || 'Token refresh failed');
    } catch (err) {
      console.error(`Failed to refresh ${provider} token:`, err);
      setError(err.message);
      return {
        success: false,
        message: err.message
      };
    }
  };

  /**
   * Get list of available OAuth providers
   * GET /api/oauth/providers
   */
  const getProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/oauth/providers`);
      const data = await response.json();

      if (data.success && data.providers) {
        return data.providers;
      }

      return [];
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      return [];
    }
  };

  /**
   * Check OAuth system health
   * GET /api/oauth/health
   */
  const checkHealth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/oauth/health`);
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Failed to check OAuth health:', err);
      return { healthy: false };
    }
  };

  // Fetch status on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchStatus();
    }
  }, []);

  return {
    integrations,
    loading,
    error,
    connect,
    disconnect,
    refresh,
    fetchStatus,
    getProviders,
    checkHealth
  };
}
